import { adminClient, ApiError } from './api-client';

/** Keycloak client representation */
export interface KeycloakClient {
  id: string;
  clientId: string;
  name: string;
  description: string;
  enabled: boolean;
  publicClient: boolean;
  secret?: string;
  rootUrl?: string;
  baseUrl?: string;
  redirectUris: string[];
  webOrigins: string[];
  attributes: Record<string, string>;
}

/** Fetch all realm clients (admin-only — proxies through /api/admin). */
export function fetchClients(token: string): Promise<KeycloakClient[]> {
  return adminClient(token).get('/clients?max=100');
}

/**
 * Fetch the app catalog visible to the current user. Unlike fetchClients this
 * hits the non-admin /api/catalog endpoint, so regular users get the apps they
 * are allowed to see (their entitled apps + visible request-access apps).
 */
export async function fetchAppCatalog(token: string): Promise<KeycloakClient[]> {
  const res = await fetch('/api/catalog/apps', {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new ApiError(res.status, `GET /api/catalog/apps: ${body}`);
  }
  const data = (await res.json()) as { clients: KeycloakClient[] };
  return data.clients;
}

/** Create a new OAuth2 client, returning the server-assigned ID */
export function createClient(token: string, config: Partial<KeycloakClient>): Promise<string> {
  return adminClient(token).postForId('/clients', config);
}

/** Update an existing client by its server ID */
export function updateClient(token: string, clientId: string, config: Partial<KeycloakClient>): Promise<void> {
  return adminClient(token).put(`/clients/${clientId}`, config);
}

/** Fetch the current secret for a confidential client */
export async function fetchClientSecret(token: string, clientId: string): Promise<string> {
  const data = await adminClient(token).get<{ value: string }>(`/clients/${clientId}/client-secret`);
  return data.value;
}

/** Regenerate and return a new secret for a confidential client */
export async function regenerateClientSecret(token: string, clientId: string): Promise<string> {
  const data = await adminClient(token).post<{ value: string }>(`/clients/${clientId}/client-secret`);
  return data.value;
}

/** Enable a client */
export function enableClient(token: string, clientId: string): Promise<void> {
  return updateClient(token, clientId, { enabled: true } as Partial<KeycloakClient>);
}

/** Disable a client */
export function disableClient(token: string, clientId: string): Promise<void> {
  return updateClient(token, clientId, { enabled: false } as Partial<KeycloakClient>);
}

/** Delete a client */
export function deleteClient(token: string, clientId: string): Promise<void> {
  return adminClient(token).del(`/clients/${clientId}`);
}
